import { useEffect, useMemo, useState } from "react";
import usePostCreateReceiptCounter from "../../apis/tryst/create-receipt-counter-config";
import useGetQueueConfig, { getQueueConfigQueryKey } from "../../apis/tryst/get-queue-config";
import { ReactComponent as LoadingCircleIcon } from "../../assets/icons/custom/loading-circle.svg";
import InputBox from "../../molecules/input/input-box";
import Error from "../../molecules/notes/error";
import Info from "../../molecules/notes/info";
import { receipt_counter } from "../../types/apis";
import Popup from "../common/popup";
import { useQueryClient } from "@tanstack/react-query";
import { useMyAccessProfileV2 } from "../../apis/tryst/get-my-access-profile";

const ReceiptCounterPopup = ({
  clinicId,
  onClosePopup,
}: {
  clinicId: string;
  onClosePopup: () => void;
}) => {
  const { mutateAsync: createReceiptCounter } = usePostCreateReceiptCounter();
  const { data: queueConfigData, isLoading: isQueueConfigLoading } =
    useGetQueueConfig();
  const { data: queueConfig } = queueConfigData || {};
  const { receipt_counter } = queueConfig || {};
  const [receiptCounter, setReceiptCounter] = useState<receipt_counter | null>(
    null
  );
  const [error, setError] = useState<string>("");
  const [info, setInfo] = useState<string>("");

  const queryClient = useQueryClient();
  const { data: myAccessProfile } = useMyAccessProfileV2();
  const businessId = myAccessProfile?.business?.id || "";

  const _receiptCounter = useMemo(() => {
    return receipt_counter?.find((rc) => rc.clinic_id === clinicId);
  }, [clinicId, receipt_counter]);

  useEffect(() => {
    if (_receiptCounter) {
      setReceiptCounter(_receiptCounter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_receiptCounter]);

  const closeMe = () => {
    onClosePopup();
  };

  const onSetUp = async () => {
    if (receiptCounter === null || !clinicId) {
      return;
    }
    const response = await createReceiptCounter({
      clinic_id: clinicId,
      receipt_counter: receiptCounter?.receipt_counter,
      receipt_prefix: receiptCounter?.receipt_prefix,
    });
    if (response.success) {
      await queryClient.invalidateQueries({
        queryKey: getQueueConfigQueryKey({ businessId }),
      });
      closeMe();
    } else {
      if (response.error) {
        setError(response.error);
      }
      console.log("error", response);
    }
  };

  if (isQueueConfigLoading) {
    return (
      <Popup
        className="w-40p h-2/4"
        onClose={closeMe}
        title="Set up receipt number sequence"
      >
        <div className="w-full h-full flex justify-center items-center">
          <LoadingCircleIcon className="h-24 w-24 text-neutral-10" />
        </div>
      </Popup>
    );
  }

  return (
    <Popup
      className="w-40p h-2/4"
      onClose={closeMe}
      title="Set up receipt number sequence"
    >
      <div className="flex-auto space-y-16 overflow-auto bg-white py-16">
        <div className="flex flex-col items-center w-full space-y-16 xl:space-y-0 xl:flex-row px-16">
          <InputBox
            label="Prefix"
            required
            value={receiptCounter?.receipt_prefix}
            onChange={(e) => {
              setError("");
              if (
                _receiptCounter?.receipt_prefix !==
                receiptCounter?.receipt_prefix
              ) {
                setInfo("show");
              }
              setReceiptCounter({
                receipt_counter: receiptCounter?.receipt_counter || 0,
                clinic_id: receiptCounter?.clinic_id || "",
                receipt_prefix: e.target.value || "",
              });
            }}
            className="pl-6 rounded-r-none"
            type="text"
            placeholder="for ex. EK"
          />
          <InputBox
            error={!!error}
            label="Start From"
            required
            value={receiptCounter?.receipt_counter || ""}
            onChange={(e) => {
              setError("");
              let value = parseInt(e.target.value);

              if (e.target.value === "") {
                setError("Receipt number cannot be empty");
              }

              if (
                (value > (_receiptCounter?.receipt_counter || 0) &&
                  _receiptCounter === undefined) ||
                (_receiptCounter?.receipt_prefix !==
                  receiptCounter?.receipt_prefix &&
                  _receiptCounter !== undefined)
              ) {
                setInfo("show");
              } else if (
                value < (_receiptCounter?.receipt_counter || 0) &&
                _receiptCounter !== undefined &&
                _receiptCounter.receipt_prefix ===
                  receiptCounter?.receipt_prefix
              ) {
                setError(
                  "Receipt number cannot be less than the current value"
                );
                setInfo("");
              }

              setReceiptCounter({
                receipt_counter: value,
                clinic_id: receiptCounter?.clinic_id || "",
                receipt_prefix: receiptCounter?.receipt_prefix || "",
              });
            }}
            className="pl-6 rounded-l-none"
            type="text"
            placeholder="for ex. 0001"
          />
        </div>
      </div>
      {!!info && !error && (
        <div className="p-16">
          <Info>
            <div>
              Please note: Once used, a receipt# cannot be reused. For example{" "}
              <span className="font-700">{`${receiptCounter?.receipt_prefix}${
                _receiptCounter?.receipt_prefix ===
                receiptCounter?.receipt_prefix
                  ? _receiptCounter?.receipt_counter || ""
                  : `001`
              }`}</span>{" "}
              cannot be used again.
            </div>{" "}
          </Info>
        </div>
      )}
      {!!error && (
        <div className="p-16">
          <Error>
            <div>
              {error} i.e.{" "}
              <span className="font-700">{`${receiptCounter?.receipt_prefix}${
                receiptCounter?.receipt_counter || ""
              }`}</span>{" "}
              Please use a sequence greater than{" "}
              <span className="font-700">
                {_receiptCounter?.receipt_prefix ===
                receiptCounter?.receipt_prefix
                  ? (_receiptCounter?.receipt_counter || 0) - 1
                  : `000`}
              </span>
              .
            </div>{" "}
          </Error>
        </div>
      )}
      <div className="flex items-center justify-between p-16 border border-l-none border-b-none border-r-none border-darwin-neutral-100">
        <button
          onClick={closeMe}
          className="neutral-btn  px-12 py-8 btn--small "
        >
          <span className="w-full text-center">Cancel</span>
        </button>

        <div className="flex items-center space-x-16 ">
          <button
            disabled={
              !!error || (receiptCounter?.receipt_prefix || "").length <= 0
            }
            onClick={onSetUp}
            className="primary-btn px-12 py-8 btn--small"
          >
            <span className="w-full text-center">Set Up</span>
          </button>
        </div>
      </div>
    </Popup>
  );
};

export default ReceiptCounterPopup;
